import { produce } from "immer";

import { normalizeBase } from "@ds/model/helpers";

import { isProjectQueryOutput, isProjectArray } from "./model";

const FORM_CONTEXTS = ["edit", "wizard", "details"] as const;

const backPopulateFormationGroups = (formation: TemplateFormation) => {
  if (!formation.groups) {
    return;
  }

  const { properties, groups } = formation;

  for (const context of FORM_CONTEXTS) {
    const contextGroups = groups[context];
    if (!contextGroups) {
      continue;
    }

    contextGroups.forEach((group, groupIndex) => {
      group.fields.forEach((fieldKey, fieldIndex) => {
        const property = properties[fieldKey];
        if (!property) {
          return;
        }

        if (!property.metadata) {
          property.metadata = {};
        }

        property.metadata[context] = {
          ...property.metadata[context],
          field_order: fieldIndex + 1,
          group_order: groupIndex + 1,
          group_title: group.title,
        };
      });
    });
  }

  const tableColumns = groups.table;
  if (tableColumns) {
    tableColumns.forEach((column, index) => {
      const property = properties[column.field];
      if (!property) {
        return;
      }

      if (!property.metadata) {
        property.metadata = {};
      }

      property.metadata.table = {
        group_order: index + 1,
        column_width: column.column_width,
      };
    });
  }
};

const normalizer = (entity: Project) =>
  produce(normalizeBase(entity), (draft: Project) => {
    draft.description = entity.description || undefined;
    backPopulateFormationGroups(draft.template.experience_formation);
    draft.template.experience_formation.properties = Object.fromEntries(
      Object.entries(draft.template.experience_formation.properties).filter(
        ([_, v]: [string, ApiBaseFormationProperty]) => !v.hidden && !v.deprecated,
      ),
    );
  });

export const normalize = ((
  entityOrEntities: Project | Project[] | QueryOutput<Project>,
): Project | Project[] | QueryOutput<Project> => {
  if (isProjectQueryOutput(entityOrEntities)) {
    return produce(entityOrEntities, draft => {
      draft.items = entityOrEntities.items.map(normalizer);
    });
  }

  return isProjectArray(entityOrEntities) ? entityOrEntities.map(normalizer) : normalizer(entityOrEntities);
}) as {
  (Project: Project): Project;
  (Projects: Project[]): Project[];
  (ProjectsQueryOutput: QueryOutput<Project>): QueryOutput<Project>;
};
